// ABOUTME: Records bounded frontend performance diagnostics for tracked flows.
// ABOUTME: Selects safe route and endpoint keys without logging request payloads.
import axios from "axios";
import { Auth } from "aws-amplify";
import * as ReactDeviceDetect from "react-device-detect";

type TrackedPathMatcher = {
    key: string;
    pattern: RegExp;
};

const TRACKED_ROUTES: TrackedPathMatcher[] = [
    {
        key: "/login",
        pattern: /^\/login\/?$/
    },
    {
        key: "/job/list",
        pattern: /^\/job\/list\/?$/
    },
    {
        key: "/job/view/*",
        pattern: /^\/job\/view\/[^/]+(?:\/[^/]+)?\/?$/
    },
    {
        key: "/applicant/list",
        pattern: /^\/applicant\/list\/?$/
    }
];

const TRACKED_ENDPOINTS: TrackedPathMatcher[] = [
    {
        key: "/general/listroutes",
        pattern: /^\/general\/listroutes$/
    },
    {
        key: "/job/list",
        pattern: /^\/job\/list(?:\/.*)?$/
    },
    {
        key: "/job/view/*",
        pattern: /^\/job\/view(?:\/.*)?$/
    },
    {
        key: "/applicant/list",
        pattern: /^\/applicant\/list(?:\/.*)?$/
    },
    {
        key: "/user/getdefaultcompanyuser",
        pattern: /^\/user\/getdefaultcompanyuser$/
    }
];

const SLOW_API_THRESHOLD_MS = 1500;
const SLOW_INITIAL_NAVIGATION_THRESHOLD_MS = 2500;
const SLOW_ROUTE_TRANSITION_THRESHOLD_MS = 150;
const SESSION_LIMIT_KEY = "compleo_frontend_performance_log_count";
const SESSION_LIMIT_MAX = 20;
const DUPLICATE_WINDOW_MS = 5 * 60 * 1000;
const NETWORK_FAILURE_SESSION_LIMIT_KEY =
    "compleo_frontend_network_failure_log_count";
const NETWORK_FAILURE_SESSION_LIMIT_MAX = 100;
const NETWORK_FAILURE_MINUTE_LIMIT_MAX = 10;
// AUTH_CONTEXT_STORAGE_KEY is an implicit coupling with the auth context storage module.
// If this key is renamed in the source module (CompleoContext provider), this constant
// must be updated in sync — otherwise the logger silently falls back to no user context.
// Source: apps/frontend/src/customHooks/useCompleoContext (or equivalent context provider).
const AUTH_CONTEXT_STORAGE_KEY = "CompleoContext_companyUserData";
const BOOTSTRAP_ENDPOINTS_WITH_UNSTABLE_CONTEXT = new Set([
    "/general/listroutes",
    "/user/getdefaultcompanyuser"
]);
const duplicateCache = new Map<string, number>();
let inMemorySessionCount = 0;
let inMemoryNetworkFailureSessionCount = 0;
let networkFailureWindowStartedAt = 0;
let networkFailureWindowCount = 0;

type PerformanceEventType =
    | "slow_api"
    | "api_no_response"
    | "slow_initial_navigation"
    | "slow_route_transition";

export type NetworkFailureReason = "timeout" | "network_error" | "aborted";

// Must match the shared frontend performance contract in packages/shared-types
// (performanceNextHopProtocol). Declared here so apiTiming reuses this runtime
// allowlist instead of keeping a second hand-maintained copy.
const NEXT_HOP_PROTOCOLS = ["h2", "h3", "http/1.1", "http/1.0"] as const;

export type NextHopProtocol = (typeof NEXT_HOP_PROTOCOLS)[number];

export const isKnownNextHopProtocol = (
    value: string
): value is NextHopProtocol =>
    (NEXT_HOP_PROTOCOLS as readonly string[]).includes(value);

// Why detailed Resource Timing milestones are missing for a slow call.
// Produced by useApi/apiTiming; must match the shared frontend performance
// contract in packages/shared-types.
export type ResourceTimingUnavailableReason =
    | "no_request_id"
    | "entry_not_found"
    | "lookup_failed"
    | "milestones_unavailable"
    | "invalid_milestones";

type PerformanceLogPayload = {
    eventType: PerformanceEventType;
    durationMs: number;
    routePath: string;
    previousRoutePath?: string;
    endpoint?: string;
    verb?: string;
    statusCode?: number;
    errorMessage?: string;
    durationBucket?: string;
    navigationType?: string;
    domInteractiveMs?: number;
    domCompleteMs?: number;
    responseEndMs?: number;
    loadEventEndMs?: number;
    requestId?: string;
    clientFinishedAt?: string;
    transportStartedAtEpochMs?: number;
    authDurationMs?: number;
    transportDurationMs?: number;
    processingDurationMs?: number;
    browserPostResponseDurationMs?: number;
    browserRequestStartedAtEpochMs?: number;
    browserResponseEndedAtEpochMs?: number;
    browserQueueDurationMs?: number;
    responseWaitDurationMs?: number;
    responseTransferDurationMs?: number;
    resourceTimingAvailable?: boolean;
    resourceTimingUnavailableReason?: ResourceTimingUnavailableReason;
    networkFailureReason?: NetworkFailureReason;
    navigatorOnline?: boolean;
    dnsDurationMs?: number;
    connectDurationMs?: number;
    tlsDurationMs?: number;
    nextHopProtocol?: NextHopProtocol;
};

type StoredAuthContext = {
    company?: {
        companyId?: number;
        companyFancyName?: string | null;
        companyName?: string | null;
    };
    user?: string | null;
};

const readStoredAuthContext = (): StoredAuthContext | null => {
    try {
        const data = localStorage.getItem(AUTH_CONTEXT_STORAGE_KEY);
        if (!data) {
            return null;
        }
        return JSON.parse(data);
    } catch {
        return null;
    }
};

const getCurrentRoutePath = () => {
    try {
        return window.location.pathname || "/";
    } catch {
        return "/";
    }
};

const normalizeEndpoint = (endpoint: string, fullUrl: boolean) => {
    if (!endpoint) {
        return "";
    }

    if (!fullUrl) {
        return endpoint.split("?")[0];
    }

    try {
        return new URL(endpoint).pathname;
    } catch {
        return endpoint.split("?")[0];
    }
};

const findTrackedPathKey = (
    value: string,
    trackedMatchers: TrackedPathMatcher[]
) => {
    const normalizedValue = value.split("?")[0];
    const matchedItem = trackedMatchers.find((item) =>
        item.pattern.test(normalizedValue)
    );

    return matchedItem?.key || null;
};

const getTrackedRouteKey = (path: string) =>
    findTrackedPathKey(path || "/", TRACKED_ROUTES);

const getTrackedEndpointKey = (endpoint: string) =>
    findTrackedPathKey(endpoint, TRACKED_ENDPOINTS);

const getDurationBucket = (durationMs: number) => {
    if (durationMs >= 5000) {
        return "5000_plus";
    }

    if (durationMs >= 3000) {
        return "3000_4999";
    }

    if (durationMs >= 2000) {
        return "2000_2999";
    }

    if (durationMs >= 1500) {
        return "1500_1999";
    }

    if (durationMs >= 500) {
        return "0500_1499";
    }

    return "0000_0499";
};

const shouldCapturePerformanceEvent = (key: string) => {
    const now = Date.now();
    const lastCapturedAt = duplicateCache.get(key);
    if (lastCapturedAt && now - lastCapturedAt < DUPLICATE_WINDOW_MS) {
        return false;
    }

    try {
        const sessionCount = Number(
            sessionStorage.getItem(SESSION_LIMIT_KEY) || "0"
        );
        if (sessionCount >= SESSION_LIMIT_MAX) {
            return false;
        }
        sessionStorage.setItem(SESSION_LIMIT_KEY, String(sessionCount + 1));
    } catch {
        if (inMemorySessionCount >= SESSION_LIMIT_MAX) {
            return false;
        }
        inMemorySessionCount += 1;
    }

    duplicateCache.set(key, now);
    return true;
};

const shouldCaptureNetworkFailure = () => {
    const now = Date.now();
    if (
        now < networkFailureWindowStartedAt ||
        now - networkFailureWindowStartedAt >= 60_000
    ) {
        networkFailureWindowStartedAt = now;
        networkFailureWindowCount = 0;
    }
    if (networkFailureWindowCount >= NETWORK_FAILURE_MINUTE_LIMIT_MAX) {
        return false;
    }

    try {
        const sessionCount = Number(
            sessionStorage.getItem(NETWORK_FAILURE_SESSION_LIMIT_KEY) || "0"
        );
        if (sessionCount >= NETWORK_FAILURE_SESSION_LIMIT_MAX) return false;
        sessionStorage.setItem(
            NETWORK_FAILURE_SESSION_LIMIT_KEY,
            String(sessionCount + 1)
        );
    } catch {
        if (
            inMemoryNetworkFailureSessionCount >=
            NETWORK_FAILURE_SESSION_LIMIT_MAX
        ) {
            return false;
        }
        inMemoryNetworkFailureSessionCount += 1;
    }

    networkFailureWindowCount += 1;
    return true;
};

// Undeliverable posts must still count against the minute attempt limit. Give
// back only the session budget so recovery can be observed after an outage.
const refundNetworkFailureSessionBudget = () => {
    try {
        const sessionCount = Number(
            sessionStorage.getItem(NETWORK_FAILURE_SESSION_LIMIT_KEY) || "0"
        );
        if (sessionCount > 0) {
            sessionStorage.setItem(
                NETWORK_FAILURE_SESSION_LIMIT_KEY,
                String(sessionCount - 1)
            );
        }
        return;
    } catch {
        // sessionStorage unavailable: refund the in-memory fallback counter.
    }
    inMemoryNetworkFailureSessionCount = Math.max(
        0,
        inMemoryNetworkFailureSessionCount - 1
    );
};

const getAuthHeader = async () => {
    try {
        return {
            Authorization: (await Auth.currentSession())
                .getIdToken()
                .getJwtToken()
        };
    } catch {
        return {};
    }
};

const shouldOmitStoredAuthContext = (payload: PerformanceLogPayload) => {
    // /login and untracked pages carry no trustworthy company context: the
    // user may be logged out or the page may predate the context load. Omit
    // stored identity there for every event type, so identity attachment
    // depends on the event's route, never on which page the user was on.
    if (payload.routePath === "/login" || payload.routePath === "/untracked") {
        return true;
    }

    return (
        payload.eventType === "slow_api" &&
        !!payload.endpoint &&
        BOOTSTRAP_ENDPOINTS_WITH_UNSTABLE_CONTEXT.has(payload.endpoint)
    );
};

const postPerformanceLog = async (payload: PerformanceLogPayload) => {
    const storedAuthContext = shouldOmitStoredAuthContext(payload)
        ? null
        : readStoredAuthContext();
    const companyId = storedAuthContext?.company?.companyId;
    const companyName =
        storedAuthContext?.company?.companyFancyName ||
        storedAuthContext?.company?.companyName ||
        "";
    const userEmail = storedAuthContext?.user || "";
    const browserInfo = {
        browserName: ReactDeviceDetect.browserName,
        browserVersion: ReactDeviceDetect.browserVersion,
        osName: ReactDeviceDetect.osName,
        osVersion: ReactDeviceDetect.osVersion,
        engineName: ReactDeviceDetect.engineName,
        engineVersion: ReactDeviceDetect.engineVersion,
        fullBrowserVersion: ReactDeviceDetect.fullBrowserVersion
    };

    await axios.post(
        `${import.meta.env.VITE_API_ADDRESS}/log/logfrontendperformance`,
        {
            companyId,
            companyName,
            userEmail,
            source: "admin",
            ...payload,
            durationMs: Math.round(payload.durationMs),
            durationBucket:
                payload.durationBucket || getDurationBucket(payload.durationMs),
            ...browserInfo
        },
        {
            headers: await getAuthHeader(),
            timeout: 5000
        }
    );
};

export const isTrackedClientRoute = (path: string) =>
    getTrackedRouteKey(path) !== null;

export const isTrackedApiEndpoint = (endpoint: string) =>
    getTrackedEndpointKey(endpoint) !== null;

// Slow-API request phases measured by apiTiming. Declared once so the payload
// type, this signature and the forwarded payload cannot drift apart.
type SlowApiTimingPhases = Pick<
    PerformanceLogPayload,
    | "requestId"
    | "clientFinishedAt"
    | "transportStartedAtEpochMs"
    | "authDurationMs"
    | "transportDurationMs"
    | "processingDurationMs"
    | "browserPostResponseDurationMs"
    | "browserRequestStartedAtEpochMs"
    | "browserResponseEndedAtEpochMs"
    | "browserQueueDurationMs"
    | "responseWaitDurationMs"
    | "responseTransferDurationMs"
    | "resourceTimingAvailable"
    | "resourceTimingUnavailableReason"
    | "dnsDurationMs"
    | "connectDurationMs"
    | "tlsDurationMs"
    | "nextHopProtocol"
>;

export const captureSlowApiDiagnostic = async ({
    routePath,
    endpoint,
    verb,
    durationMs,
    statusCode,
    fullUrl,
    errorMessage,
    ...timingPhases
}: {
    routePath: string;
    endpoint: string;
    verb: string;
    durationMs: number;
    statusCode?: number;
    fullUrl: boolean;
    errorMessage?: string;
} & SlowApiTimingPhases) => {
    if (durationMs < SLOW_API_THRESHOLD_MS || navigator.onLine === false) {
        return;
    }

    const trackedRoutePath = getTrackedRouteKey(
        routePath || getCurrentRoutePath()
    );
    const normalizedEndpoint = normalizeEndpoint(endpoint, fullUrl);
    const endpointKey = getTrackedEndpointKey(normalizedEndpoint);

    if (
        normalizedEndpoint.startsWith("/log/") ||
        (!trackedRoutePath && !endpointKey)
    ) {
        return;
    }

    const dedupeKey = `slow_api:${trackedRoutePath || "untracked"}:${verb}:${endpointKey || "untracked"}:${statusCode || "none"}`;
    if (!shouldCapturePerformanceEvent(dedupeKey)) {
        return;
    }

    try {
        await postPerformanceLog({
            eventType: "slow_api",
            routePath: trackedRoutePath || "/untracked",
            endpoint: endpointKey || undefined,
            verb,
            durationMs,
            statusCode,
            errorMessage,
            ...timingPhases
        });
    } catch {
        // Diagnostics must never break user flows.
    }
};

// A failure that never got an HTTP response is the signal itself: capture it
// even when quick or while the browser reports itself offline, under budgets
// separate from slow events (no dedupe) so an outage can neither starve the
// slow-event budget nor amplify itself through this telemetry.
export const captureNetworkFailureDiagnostic = async ({
    routePath,
    endpoint,
    verb,
    durationMs,
    fullUrl,
    networkFailureReason,
    // Deliberately dropped, not forwarded: a no-response failure has no
    // status, and the axios message can embed the full token-bearing URL.
    statusCode: _statusCode,
    errorMessage: _errorMessage,
    ...timingPhases
}: {
    routePath: string;
    endpoint: string;
    verb: string;
    durationMs: number;
    statusCode?: number;
    fullUrl: boolean;
    errorMessage?: string;
    networkFailureReason: NetworkFailureReason;
} & SlowApiTimingPhases) => {
    const normalizedEndpoint = normalizeEndpoint(endpoint, fullUrl);
    // External URLs and pre-transport setup errors never faced the Compleo
    // API; the telemetry endpoints must not observe themselves.
    if (
        normalizedEndpoint.startsWith("/log/") ||
        fullUrl ||
        !normalizedEndpoint.startsWith("/")
    ) {
        return;
    }

    if (!shouldCaptureNetworkFailure()) {
        return;
    }

    const trackedRoutePath = getTrackedRouteKey(
        routePath || getCurrentRoutePath()
    );
    const endpointKey = getTrackedEndpointKey(normalizedEndpoint);
    const navigatorOnline =
        typeof navigator.onLine === "boolean" ? navigator.onLine : undefined;

    try {
        await postPerformanceLog({
            eventType: "api_no_response",
            routePath: trackedRoutePath || "/untracked",
            // Only the bounded allowlist key; untracked endpoint paths can
            // embed identifiers, so they are not recorded.
            endpoint: endpointKey || undefined,
            verb,
            durationMs,
            networkFailureReason,
            navigatorOnline,
            ...timingPhases
        });
    } catch {
        refundNetworkFailureSessionBudget();
        // Diagnostics must never break user flows.
    }
};

export const captureSlowInitialNavigation = async (routePath: string) => {
    if (navigator.onLine === false) {
        return;
    }

    const trackedRoutePath = getTrackedRouteKey(routePath);
    if (!trackedRoutePath) {
        return;
    }

    const navigationEntry = performance.getEntriesByType(
        "navigation"
    )[0] as PerformanceNavigationTiming | undefined;

    if (
        !navigationEntry ||
        navigationEntry.duration < SLOW_INITIAL_NAVIGATION_THRESHOLD_MS
    ) {
        return;
    }

    const dedupeKey = `slow_initial_navigation:${trackedRoutePath}`;
    if (!shouldCapturePerformanceEvent(dedupeKey)) {
        return;
    }

    try {
        await postPerformanceLog({
            eventType: "slow_initial_navigation",
            routePath: trackedRoutePath,
            durationMs: navigationEntry.duration,
            navigationType: navigationEntry.type,
            domInteractiveMs: Math.round(navigationEntry.domInteractive),
            domCompleteMs: Math.round(navigationEntry.domComplete),
            responseEndMs: Math.round(navigationEntry.responseEnd),
            loadEventEndMs: Math.round(navigationEntry.loadEventEnd)
        });
    } catch {
        // Diagnostics must never break user flows.
    }
};

export const captureSlowRouteTransition = async ({
    routePath,
    previousRoutePath,
    durationMs
}: {
    routePath: string;
    previousRoutePath?: string;
    durationMs: number;
}) => {
    if (navigator.onLine === false) {
        return;
    }

    const trackedRoutePath = getTrackedRouteKey(routePath);
    if (!trackedRoutePath || durationMs < SLOW_ROUTE_TRANSITION_THRESHOLD_MS) {
        return;
    }

    const trackedPreviousRoutePath = previousRoutePath
        ? getTrackedRouteKey(previousRoutePath)
        : null;

    const dedupeKey = `slow_route_transition:${trackedPreviousRoutePath || "entry"}:${trackedRoutePath}`;
    if (!shouldCapturePerformanceEvent(dedupeKey)) {
        return;
    }

    try {
        await postPerformanceLog({
            eventType: "slow_route_transition",
            routePath: trackedRoutePath,
            previousRoutePath: trackedPreviousRoutePath || undefined,
            durationMs,
            navigationType: "spa"
        });
    } catch {
        // Diagnostics must never break user flows.
    }
};
